"use client";

import React from "react";

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage } from "@/components/ui/avatar";

import { Copy, Pencil, Check } from "lucide-react";

import { useCopyToClipboard } from "usehooks-ts";

const WALLET_ADDRESS = "0x0000000000000000000000000000000000";

export function InfoCard() {
  const [copiedText, copy] = useCopyToClipboard();
  const [isCopied, setIsCopied] = React.useState(false);

  function handleCopy() {
    copy(WALLET_ADDRESS);
    setIsCopied(true);

    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  }

  return (
    <Card className="bg-transparent">
      <CardHeader className="px-5 pt-3.5 pb-2">
        <CardTitle className="mb-6 text-lg">Personal Information</CardTitle>
        <div className="flex items-center gap-4">
          <div className="relative">
            <Avatar className="size-11 rounded-full">
              <AvatarImage src="/avatars/sajadevo.png" alt="sajadevo" />
            </Avatar>
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    className="absolute -right-0.5 -bottom-0.5 grid size-4 place-items-center"
                    aria-label="Edit profile avatar"
                  >
                    <Pencil className="text-primary-foreground size-2!" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Edit</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div>
            <p className="mb-1 text-sm font-medium text-white">
              Profile Avatar
            </p>
            <p className="text-foreground text-xs">Maximum up to 5MB</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="mt-6 px-5 pb-0">
        <div className="space-y-6">
          <div className="w-full">
            <Label htmlFor="name" className="mb-2">
              Display Name
            </Label>
            <Input
              id="name"
              placeholder="e.g. John Doe"
              className="bg-background"
            />
          </div>
          <div className="w-full">
            <Label htmlFor="email" className="mb-2">
              Email Address
            </Label>
            <Input
              type="email"
              id="email"
              placeholder="e.g. someone@example.com"
              className="bg-background"
            />
          </div>
          <div className="w-full">
            <Label htmlFor="wallet-address" className="mb-2">
              Wallet Address
            </Label>
            <div className="relative">
              <Input
                readOnly
                id="wallet-address"
                className="bg-background pr-10"
                defaultValue={WALLET_ADDRESS}
              />
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleCopy}
                      className="absolute top-1.5 right-1.5 size-7 rounded"
                      aria-label="Copy wallet address"
                    >
                      {isCopied ? (
                        <Check className="size-4" />
                      ) : (
                        <Copy className="size-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copy</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-end pt-6">
        <Button size="lg" className="px-4">
          Save
        </Button>
      </CardFooter>
    </Card>
  );
}
